/**
 * renderStateSnapshot — coherence/revision receipt for a chat turn (PR1).
 *
 * WHAT THIS DELIBERATELY DOES **NOT** REPORT
 * ------------------------------------------
 * Per-element visibility. Two attempts at deriving it from shared helpers were
 * both wrong, in the same way, one layer apart:
 *
 *   1. the backend read an empty `visible_elements` as "nothing visible", while
 *      the frontend reads it as "no restriction — everything visible";
 *   2. deriving it here from `getStageVisibilityFlags` + the overlay predicate
 *      still disagrees with the renderers. The line renderer computes
 *      `isShowAllStage = show_all_with_original_style || !hasVisibilityConstraints`
 *      and draws EVERY line when `visible_elements` is empty
 *      (InteractiveGraph.js:2980, 3209-3212) — regardless of `showData`. A
 *      snapshot built on the generic flags reports those lines hidden while the
 *      student is looking straight at them.
 *
 * There are 17 renderers and they do not share one visibility rule, so no
 * generic helper can speak for them. A registry that is confidently wrong is
 * worse than no registry: it would let the tutor assert things about the screen
 * that are false.
 *
 * PR1 therefore carries only what can be stated correctly — identity and
 * revisions, which is all the stale/retry race control actually needs. The real
 * per-element receipt is a renderer-owned artifact and belongs to PR2, where D5
 * needs it to resolve "hide the blue bar". Schema version 2 will add it.
 */

export const RENDER_STATE_SCHEMA_VERSION = 1;

/**
 * @param {object} args
 * @param {number} args.stageIndex          current stage index
 * @param {number} args.stageRevision       monotonic; bumped on every stage change
 * @param {number} args.interactionRevision monotonic; bumped on any in-stage view change
 * @param {string} args.trigViewMode
 * @param {string} args.funcDisplayMode
 * @returns {object|null} null when there is nothing meaningful to report
 */
export function buildRenderStateSnapshot({
  stageIndex,
  stageRevision = 0,
  interactionRevision = 0,
  trigViewMode = null,
  funcDisplayMode = null,
  graphId = null,
  sessionId = null,
} = {}) {
  if (!Number.isInteger(stageIndex) || stageIndex < 0) return null;
  return {
    schema_version: RENDER_STATE_SCHEMA_VERSION,
    // Identity is carried DIRECTLY, not represented by a counter bump. When the
    // Dashboard swaps session/graph mid-flight, a passive effect increments the
    // revision only on the next render — until then the parent still holds the
    // old snapshot, so an in-flight reply could compare equal and be applied to
    // a different graph. A remount also resets counters to zero.
    graph_id: graphId == null ? null : String(graphId),
    session_id: sessionId == null ? null : String(sessionId),
    stage_index: stageIndex,
    stage_revision: Number.isInteger(stageRevision) ? stageRevision : 0,
    interaction_revision: Number.isInteger(interactionRevision) ? interactionRevision : 0,
    display_modes: { trig: trigViewMode || null, func: funcDisplayMode || null },
  };
}

/**
 * Do two snapshots describe the same screen?
 *
 * Both revisions are true monotonic counters, so this cannot collide the way
 * comparing derived values did: an earlier version used
 * `Object.keys(overrides).length + (trig === 'curve_only' ? 0 : 1)`, under which
 * `unit_circle` and `dual_pane` both scored 1 and any two override maps of equal
 * size were indistinguishable — a screen change that compared equal.
 */
export function isSameScreen(a, b) {
  if (!a || !b) return false;
  // Identity first: a different graph or session is a different screen no
  // matter what the revisions say.
  if ((a.graph_id ?? null) !== (b.graph_id ?? null)) return false;
  if ((a.session_id ?? null) !== (b.session_id ?? null)) return false;
  return (
    a.stage_index === b.stage_index &&
    a.stage_revision === b.stage_revision &&
    a.interaction_revision === b.interaction_revision &&
    (a.display_modes?.trig ?? null) === (b.display_modes?.trig ?? null) &&
    (a.display_modes?.func ?? null) === (b.display_modes?.func ?? null)
  );
}
